import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api } from "../services/api";
import { entityAPI } from "../services/entityCache";
import {
  ENRICHMENT_ANCHOR_TYPE_OPTIONS,
  enrichmentAnchorTypeLabel,
  isEnrichmentAnchorType,
  normalizeEnrichmentAnchorType,
} from "../config/enrichmentAnchorTypes";
import { useDeviceDetection } from "../hooks/useDeviceDetection";
import { AddKnowledgeWizardBackButton } from "./addKnowledgeWizardUi";
import { revealGuideAttentionTarget } from "../v2/utils/scrollShell";

const ANCHOR_SEARCH_MIN_CHARS = 2;
const ANCHOR_SEARCH_DEBOUNCE_MS = 300;

const SURFACE_KIND_OPTIONS = [
  { value: "architecture_principles", label: "Architecture principles" },
  { value: "design_patterns", label: "Design patterns" },
  { value: "sop_runbook", label: "SOPs & runbooks" },
  { value: "security_standards", label: "Security standards" },
  { value: "knowledge_article", label: "Knowledge articles" },
  { value: "domain_knowledge_base", label: "General / other" },
];

/**
 * Step 2 for Add knowledge: pick or create the anchor (architecture entity or category)
 * after scope was chosen in the previous step.
 */
const AddKnowledgeAnchorPanel = ({
  scopeType,
  anchor,
  onAnchorChange,
  onContinue,
  onBack,
  embedded = true,
  initialCreateName = "",
  startInCreateMode = false,
  highlightCreate = false,
  highlightContinue = false,
}) => {
  const [mode, setMode] = useState(
    startInCreateMode || initialCreateName ? "create" : "existing"
  );
  const [search, setSearch] = useState("");
  const [entityResults, setEntityResults] = useState([]);
  const [pinnedEntities, setPinnedEntities] = useState([]);
  const [allSurfaces, setAllSurfaces] = useState([]);
  const [searchingEntities, setSearchingEntities] = useState(false);
  const [loadingSurfaces, setLoadingSurfaces] = useState(false);
  const [selectedId, setSelectedId] = useState(anchor?.id || "");
  const [newName, setNewName] = useState(
    () => String(initialCreateName || "").trim()
  );
  const [newDescription, setNewDescription] = useState("");
  const [filterEntityType, setFilterEntityType] = useState("application");
  const [newEntityType, setNewEntityType] = useState("application");
  const [newSurfaceKind, setNewSurfaceKind] = useState("domain_knowledge_base");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const loadedSurfacesRef = useRef(false);
  const currentTenantRef = useRef(null);
  const entitySearchRequestRef = useRef(0);

  const { isMobile } = useDeviceDetection();
  const isEntityScope = scopeType === "entity";
  const activeEntityType = normalizeEnrichmentAnchorType(
    filterEntityType || "application"
  );

  const getCurrentTenantId = useCallback(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.organization_id || user?.tenant_id || null;
    } catch {
      return null;
    }
  }, []);

  const ensureSurfacesLoaded = useCallback(async () => {
    const currentTenant = getCurrentTenantId();
    if (
      currentTenantRef.current !== null &&
      currentTenantRef.current !== currentTenant
    ) {
      loadedSurfacesRef.current = false;
      setAllSurfaces([]);
    }
    if (loadedSurfacesRef.current || loadingSurfaces) return;

    setLoadingSurfaces(true);
    setError("");
    try {
      const surfaces = await api.listKnowledgeSurfaces();
      setAllSurfaces(Array.isArray(surfaces) ? surfaces : []);
      loadedSurfacesRef.current = true;
    } catch (e) {
      setError(e?.message || "Failed to load knowledge categories.");
      setAllSurfaces([]);
      loadedSurfacesRef.current = true;
    } finally {
      setLoadingSurfaces(false);
    }
  }, [getCurrentTenantId, loadingSurfaces]);

  useEffect(() => {
    if (!isEntityScope || mode !== "existing") return;

    const q = search.trim();
    if (q.length < ANCHOR_SEARCH_MIN_CHARS) {
      setEntityResults([]);
      setSearchingEntities(false);
      return;
    }

    const requestId = ++entitySearchRequestRef.current;
    setSearchingEntities(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const entities = await entityAPI.searchEnrichmentAnchors(q, {
            limit: 25,
            entityTypes: [activeEntityType],
          });
          if (entitySearchRequestRef.current !== requestId) return;
          setEntityResults(Array.isArray(entities) ? entities : []);
          setError("");
        } catch (e) {
          if (entitySearchRequestRef.current !== requestId) return;
          setEntityResults([]);
          setError(e?.message || "Failed to search architecture entities.");
        } finally {
          if (entitySearchRequestRef.current === requestId) {
            setSearchingEntities(false);
          }
        }
      })();
    }, ANCHOR_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [isEntityScope, mode, search, activeEntityType]);

  useEffect(() => {
    if (!isEntityScope && mode === "existing") {
      void ensureSurfacesLoaded();
    }
  }, [isEntityScope, mode, ensureSurfacesLoaded]);

  const entities = useMemo(() => {
    const q = search.trim();
    const byId = new Map();
    for (const ent of pinnedEntities) {
      if (!ent?.id) continue;
      const entType = normalizeEnrichmentAnchorType(
        ent.entity_type || ent.type || ""
      );
      if (entType && entType !== activeEntityType) continue;
      byId.set(ent.id, ent);
    }
    if (q.length >= ANCHOR_SEARCH_MIN_CHARS) {
      for (const ent of entityResults) {
        if (ent?.id && !byId.has(ent.id)) byId.set(ent.id, ent);
      }
    }
    return [...byId.values()].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, {
        sensitivity: "base",
      })
    );
  }, [entityResults, pinnedEntities, search, activeEntityType]);

  const handleFilterEntityTypeChange = useCallback(
    (nextType) => {
      const normalized = normalizeEnrichmentAnchorType(nextType) || "application";
      setFilterEntityType(normalized);
      setNewEntityType(normalized);
      setEntityResults([]);
      setSelectedId("");
      // Clear selection when type changes so user does not continue with a mismatched anchor.
      if (anchor?.id) {
        onAnchorChange?.(null);
      }
    },
    [anchor?.id, onAnchorChange]
  );

  const surfaces = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = allSurfaces;
    if (q) {
      list = list.filter(
        (s) =>
          String(s.surface_name || "")
            .toLowerCase()
            .includes(q) ||
          String(s.surface_description || "")
            .toLowerCase()
            .includes(q)
      );
    }
    return [...list].sort((a, b) =>
      String(a.surface_name || "").localeCompare(
        String(b.surface_name || ""),
        undefined,
        { sensitivity: "base" }
      )
    );
  }, [allSurfaces, search]);

  useEffect(() => {
    if (anchor?.id) {
      // Resume / guide restore: show selected entity + Continue, not create form.
      setMode("existing");
      setSelectedId(anchor.id);
    }
    if (anchor?.id && anchor?.name && isEntityScope) {
      const entityType = normalizeEnrichmentAnchorType(
        anchor.entityType || anchor.type || "application"
      );
      if (entityType && isEnrichmentAnchorType(entityType)) {
        setFilterEntityType(entityType);
        setNewEntityType(entityType);
      }
      setPinnedEntities((prev) => {
        if (prev.some((a) => a.id === anchor.id)) return prev;
        return [
          {
            id: anchor.id,
            name: anchor.name,
            description: anchor.description || null,
            entity_type: entityType,
            type: entityType,
          },
          ...prev,
        ];
      });
    }
  }, [
    anchor?.id,
    anchor?.name,
    anchor?.description,
    anchor?.entityType,
    anchor?.type,
    isEntityScope,
  ]);

  const cardClass = embedded
    ? "rounded-xl border border-slate-200 bg-white shadow-sm"
    : "rounded-xl border border-gray-600 bg-gray-800/90";

  const publishAnchor = useCallback(
    (next) => {
      onAnchorChange?.({
        scopeType,
        ...next,
      });
    },
    [onAnchorChange, scopeType]
  );

  const handleSelectEntity = (id) => {
    setSelectedId(id);
    const ent =
      entities.find((a) => a.id === id) ||
      pinnedEntities.find((a) => a.id === id);
    if (ent) {
      const entityType = normalizeEnrichmentAnchorType(
        ent.entity_type || ent.type || "application"
      );
      publishAnchor({
        id: ent.id,
        name: ent.name || enrichmentAnchorTypeLabel(entityType),
        entityType,
        type: entityType,
      });
    }
  };

  const handleSelectSurface = (id) => {
    setSelectedId(id);
    const surface =
      surfaces.find((s) => s.id === id) ||
      allSurfaces.find((s) => s.id === id);
    if (surface) {
      publishAnchor({
        id: surface.id,
        name: surface.surface_name || "Knowledge",
      });
    }
  };

  const handleQuickCreateEntity = async () => {
    const name = newName.trim();
    const entityType = normalizeEnrichmentAnchorType(newEntityType);
    if (!name) {
      setError("Enter a name for the new entity.");
      return;
    }
    if (!isEnrichmentAnchorType(entityType)) {
      setError("Choose a supported architecture entity type.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const created = await api.createEntity({
        name,
        type: entityType,
        description: newDescription.trim() || null,
        approval_status: "Approved",
      });
      const id = created?.id;
      if (!id) {
        throw new Error("Entity was created but no id was returned.");
      }
      const entry = {
        id,
        name: created?.name || name,
        description: newDescription.trim() || created?.description || null,
        entity_type: entityType,
        type: entityType,
      };
      setPinnedEntities((prev) => {
        if (prev.some((a) => a.id === id)) return prev;
        return [entry, ...prev];
      });
      setFilterEntityType(entityType);
      publishAnchor({
        id,
        name: entry.name,
        entityType,
        type: entityType,
      });
      setSelectedId(id);
      setMode("existing");
      setSearch("");
      setNewName("");
      setNewDescription("");
    } catch (e) {
      setError(e?.message || "Failed to create entity.");
    } finally {
      setBusy(false);
    }
  };

  const handleQuickCreateSurface = async () => {
    const name = newName.trim();
    if (!name) {
      setError("Enter a name for the knowledge category.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const created = await api.createKnowledgeSurface({
        surface_name: name,
        surface_kind: newSurfaceKind,
        surface_description: newDescription.trim() || null,
        is_global: true,
      });
      const id = created?.id;
      if (!id) {
        throw new Error("Category was created but no id was returned.");
      }
      setAllSurfaces((prev) => {
        if (prev.some((s) => s.id === id)) return prev;
        return [created, ...prev];
      });
      loadedSurfacesRef.current = true;
      publishAnchor({ id, name: created.surface_name || name });
      setSelectedId(id);
      setMode("existing");
      setSearch("");
      setNewName("");
      setNewDescription("");
    } catch (e) {
      setError(e?.message || "Failed to create knowledge category.");
    } finally {
      setBusy(false);
    }
  };

  const switchToCreate = () => {
    setMode("create");
    setError("");
    setNewName("");
    setNewDescription("");
    setNewEntityType(activeEntityType);
  };

  const switchToExisting = () => {
    setMode("existing");
    setError("");
    setNewName("");
    setNewDescription("");
  };

  const canContinue = Boolean(anchor?.id && anchor?.name);
  const createCtaRef = useRef(null);
  const continueCtaRef = useRef(null);

  // Guided demo: keep blinking Create / Continue above the scrollbar fold.
  useLayoutEffect(() => {
    const showCreate = Boolean(highlightCreate && !busy);
    const showContinue = Boolean(highlightContinue && canContinue);
    if (!showCreate && !showContinue) return undefined;
    const el = showContinue ? continueCtaRef.current : createCtaRef.current;
    if (!el) return undefined;
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(() => {
        revealGuideAttentionTarget(el);
      });
    });
    return () => {
      window.cancelAnimationFrame(outer);
      if (inner) window.cancelAnimationFrame(inner);
    };
  }, [busy, canContinue, highlightContinue, highlightCreate]);

  const stepTitle = isEntityScope
    ? "Which architecture entity is this for?"
    : "Where should this knowledge live?";
  const entitySearchReady = search.trim().length >= ANCHOR_SEARCH_MIN_CHARS;
  const typeLabel = enrichmentAnchorTypeLabel(activeEntityType);

  return (
    <div className={cardClass}>
      {/* One-line step header: back link, step label, question and hint all
          share a single bar instead of four stacked lines. */}
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-200 bg-slate-50/80 rounded-t-xl ${
          isMobile ? "px-3 py-2" : "px-4 py-2"
        }`}
      >
        {onBack ? (
          <AddKnowledgeWizardBackButton onClick={onBack}>
            Change knowledge type
          </AddKnowledgeWizardBackButton>
        ) : null}
        <p className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Step 2 · {isEntityScope ? "Choose entity" : "Choose category"}
        </p>
        <h3 className="text-sm font-semibold text-slate-900">{stepTitle}</h3>
      </div>

      <div className={isMobile ? "p-3 space-y-3" : "px-4 py-3 space-y-3"}>
        {mode === "existing" ? (
          <div className="space-y-2.5">
            {/* Type and search share one row on desktop (stacked on mobile)
                instead of two full-width labelled blocks. */}
            <div
              className={
                isEntityScope && !isMobile
                  ? "grid grid-cols-[210px_1fr] items-end gap-3"
                  : "space-y-2.5"
              }
            >
              {isEntityScope ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    Entity type
                  </label>
                  <select
                    value={activeEntityType}
                    onChange={(e) => handleFilterEntityTypeChange(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-red-400 focus:ring-1 focus:ring-red-300"
                  >
                    {ENRICHMENT_ANCHOR_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  {isEntityScope ? `Search ${typeLabel}` : "Search categories"}
                </label>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={
                    isEntityScope
                      ? `Type a ${typeLabel.toLowerCase()} name…`
                      : "Type a category name…"
                  }
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-red-400 focus:ring-1 focus:ring-red-300"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {isEntityScope ? (
                !entitySearchReady && entities.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-500">
                    Type at least {ANCHOR_SEARCH_MIN_CHARS} characters to search
                    {" "}
                    {typeLabel.toLowerCase()} records.
                  </p>
                ) : searchingEntities && entities.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-500">
                    Searching {typeLabel.toLowerCase()}…
                  </p>
                ) : entities.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-500">
                    No {typeLabel.toLowerCase()} matches. Try another search or create a new one.
                  </p>
                ) : (
                  entities.map((ent) => {
                    const active = selectedId === ent.id;
                    return (
                      <button
                        key={ent.id}
                        type="button"
                        onClick={() => handleSelectEntity(ent.id)}
                        className={`w-full text-left px-3 text-sm transition-colors ${
                          isMobile ? "py-2" : "py-2.5"
                        } ${
                          active
                            ? "bg-red-50 text-red-900 font-medium"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span className="block truncate">{ent.name || "Unnamed"}</span>
                        {ent.description ? (
                          <span className="block text-xs text-slate-500 mt-0.5 line-clamp-1">
                            {ent.description}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )
              ) : loadingSurfaces && surfaces.length === 0 ? (
                <p className="px-3 py-2 text-xs text-slate-500">Loading categories…</p>
              ) : surfaces.length === 0 ? (
                <p className="px-3 py-2 text-xs text-slate-500">
                  No categories yet. Create one to organize principles, SOPs, or articles.
                </p>
              ) : (
                surfaces.map((surface) => {
                  const active = selectedId === surface.id;
                  return (
                    <button
                      key={surface.id}
                      type="button"
                      onClick={() => handleSelectSurface(surface.id)}
                      className={`w-full text-left px-3 text-sm transition-colors ${
                        isMobile ? "py-2" : "py-2.5"
                      } ${
                        active
                          ? "bg-red-50 text-red-900 font-medium"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span className="block">{surface.surface_name || "Unnamed"}</span>
                      {surface.surface_description ? (
                        <span className="block text-xs text-slate-500 mt-0.5 line-clamp-1">
                          {surface.surface_description}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
            <button
              type="button"
              onClick={switchToCreate}
              className="text-xs font-medium text-blue-700 hover:text-blue-800 hover:underline"
            >
              {isEntityScope
                ? "Don't see it? Create a new entity"
                : "Don't see it? Create a new category"}
            </button>
          </div>
        ) : (
          <div className="space-y-2.5 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 p-3">
            {/* Back link and the quick-create hint share one toolbar row. */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <AddKnowledgeWizardBackButton onClick={switchToExisting}>
                Back to {isEntityScope ? "entity list" : "category list"}
              </AddKnowledgeWizardBackButton>
              <p className="text-xs text-slate-500">
                {isEntityScope
                  ? "Quick create — adds a minimal record to your map."
                  : "Create a reusable category for shared organizational knowledge."}
              </p>
            </div>
            {/* Type + name on one row; description + create action on the
                next, so the form is two compact rows instead of four. */}
            <div
              className={
                isMobile
                  ? "space-y-2.5"
                  : "grid grid-cols-[210px_1fr] items-end gap-3"
              }
            >
              {isEntityScope ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    Entity type
                  </label>
                  <select
                    value={newEntityType}
                    onChange={(e) => setNewEntityType(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {ENRICHMENT_ANCHOR_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-700">
                    Category type
                  </label>
                  <select
                    value={newSurfaceKind}
                    onChange={(e) => setNewSurfaceKind(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {SURFACE_KIND_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  {isEntityScope ? "Name" : "Category name"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={
                    isEntityScope ? "e.g. Customer Portal" : "e.g. Architecture Principles"
                  }
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div
              className={
                isMobile
                  ? "space-y-2.5"
                  : "grid grid-cols-[1fr_auto] items-end gap-3"
              }
            >
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  Short description{" "}
                  <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  rows={2}
                  placeholder="Helpful for search and browsing"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <button
                type="button"
                ref={createCtaRef}
                onClick={() =>
                  void (isEntityScope
                    ? handleQuickCreateEntity()
                    : handleQuickCreateSurface())
                }
                disabled={busy}
                className={`shrink-0 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50${
                  highlightCreate && !busy ? " example-cta-attention" : ""
                }`}
              >
                {busy
                  ? "Creating…"
                  : isEntityScope
                    ? "Create entity"
                    : "Create category"}
              </button>
            </div>
          </div>
        )}

        {error ? (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        ) : null}

        {/* Selected chip and Continue share one footer row instead of a
            full-width banner stacked above the button. */}
        <div className="flex flex-wrap items-center gap-2">
          {anchor?.id ? (
            <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900">
              <span className="shrink-0 font-medium">Selected:</span>
              <span className="truncate font-semibold">{anchor.name}</span>
              {isEntityScope && (anchor.entityType || anchor.type) ? (
                <span className="shrink-0 text-emerald-700/80">
                  ({enrichmentAnchorTypeLabel(anchor.entityType || anchor.type)})
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-xs text-slate-400">
              {isEntityScope
                ? "Select or create an entity to continue."
                : "Select or create a category to continue."}
            </span>
          )}
          <button
            type="button"
            ref={continueCtaRef}
            disabled={!canContinue}
            onClick={() => canContinue && onContinue?.()}
            className={`ml-auto shrink-0 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed${
              highlightContinue && canContinue ? " example-cta-attention" : ""
            }`}
          >
            Continue to choose how →
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddKnowledgeAnchorPanel;
